import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  User, Mail, Phone, MapPin, Briefcase, GraduationCap,
  LogOut, FileText, CheckCircle, XCircle, Edit2, Save, X
} from 'lucide-react';
import { cn } from '../lib/utils';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

interface WorkerProfile {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  job: string;
  contracts: [];
  area: string;
  diploma: string;
  driver_license: string;
  caces: string;
  caces_type: string;
  other_info: string;
  status: string;
}

export function Profile() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState<WorkerProfile | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState<WorkerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProfile();
  }, [user]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `https://x1lc-aku3-stjs.p7.xano.io/api:moW4brDB/worker/${user?.worker_id}`,
        {
          headers: {
            'Authorization': `Bearer ${user?.token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Impossible de récupérer les données du profil');
      }

      const data = await response.json();
      setProfile(data);
      setEditedProfile(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editedProfile) return;

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(
        `https://x1lc-aku3-stjs.p7.xano.io/api:moW4brDB/worker/${user?.worker_id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${user?.token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editedProfile)
        }
      );

      if (!response.ok) {
        throw new Error('Impossible de mettre à jour le profil');
      }

      const updatedProfile = await response.json();
      setProfile(updatedProfile);
      setIsEditing(false);
      toast.success('Profil mis à jour avec succès');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue';
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Déconnexion réussie');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-500 border-t-transparent" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Une erreur est survenue
          </h2>
          <p className="text-gray-600 mb-4">
            Impossible de charger votre profil. Veuillez réessayer plus tard.
          </p>
          <button
            onClick={fetchProfile}
            className="text-teal-600 hover:text-teal-700 font-medium"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Mon Profil
            </h1>
            <p className="mt-2 text-gray-600">
              Gérez vos informations personnelles et professionnelles
            </p>
          </div>
          <div className="flex gap-4">
            {isEditing ? (
              <>
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditedProfile(profile);
                  }}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <X className="h-4 w-4 mr-2" />
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-teal-600 hover:bg-teal-700 transition-colors"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Enregistrer
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Modifier
                </button>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Déconnexion
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            layout
            className="bg-white/80 backdrop-blur-xl rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Informations Personnelles
              </h2>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prénom
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedProfile?.first_name}
                        onChange={(e) => setEditedProfile(prev => ({
                          ...prev!,
                          first_name: e.target.value
                        }))}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    ) : (
                      <div className="flex items-center text-gray-900">
                        <User className="h-5 w-5 text-gray-400 mr-2" />
                        {profile.first_name}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedProfile?.last_name}
                        onChange={(e) => setEditedProfile(prev => ({
                          ...prev!,
                          last_name: e.target.value
                        }))}
                        className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                      />
                    ) : (
                      <div className="flex items-center text-gray-900">
                        <User className="h-5 w-5 text-gray-400 mr-2" />
                        {profile.last_name}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={editedProfile?.email}
                      onChange={(e) => setEditedProfile(prev => ({
                        ...prev!,
                        email: e.target.value
                      }))}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    />
                  ) : (
                    <div className="flex items-center text-gray-900">
                      <Mail className="h-5 w-5 text-gray-400 mr-2" />
                      {profile.email}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editedProfile?.phone}
                      onChange={(e) => setEditedProfile(prev => ({
                        ...prev!,
                        phone: e.target.value
                      }))}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    />
                  ) : (
                    <div className="flex items-center text-gray-900">
                      <Phone className="h-5 w-5 text-gray-400 mr-2" />
                      {profile.phone}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            layout
            className="bg-white/80 backdrop-blur-xl rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                Informations Professionnelles
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Poste Recherché
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.job}
                      onChange={(e) => setEditedProfile(prev => ({
                        ...prev!,
                        job: e.target.value
                      }))}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    />
                  ) : (
                    <div className="flex items-center text-gray-900">
                      <Briefcase className="h-5 w-5 text-gray-400 mr-2" />
                      {profile.job}
                    </div>
                  )}
                </div>

              <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">
    Types de Contrats
  </label>

  {isEditing ? (
    <div className="grid grid-cols-2 gap-2">
      {['CDI', 'CDD', 'Intérim', 'Stage', 'Alternance'].map((type) => (
        <label key={type} className="flex items-center gap-2 text-gray-700">
          <input
            type="checkbox"
            checked={editedProfile?.contracts.includes(type)}
            onChange={() => {
              const current = editedProfile?.contracts || [];
              const updated = current.includes(type)
                ? current.filter(c => c !== type)
                : [...current, type];
              setEditedProfile(prev => ({
                ...prev!,
                contracts: updated
              }));
            }}
            className="h-4 w-4 text-teal-600 border-gray-300 rounded"
          />
          <span>{type}</span>
        </label>
      ))}
    </div>
  ) : (
    <div className="flex items-center text-gray-900">
      <FileText className="h-5 w-5 text-gray-400 mr-2" />
      {Array.isArray(profile.contracts) && profile.contracts.length > 0
        ? profile.contracts.join(', ')
        : 'Non spécifié'}
    </div>
  )}
</div>


                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zone Géographique
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.area}
                      onChange={(e) => setEditedProfile(prev => ({
                        ...prev!,
                        area: e.target.value
                      }))}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    />
                  ) : (
                    <div className="flex items-center text-gray-900">
                      <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                      {profile.area}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Diplôme
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedProfile?.diploma}
                      onChange={(e) => setEditedProfile(prev => ({
                        ...prev!,
                        diploma: e.target.value
                      }))}
                      className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    />
                  ) : (
                    <div className="flex items-center text-gray-900">
                      <GraduationCap className="h-5 w-5 text-gray-400 mr-2" />
                      {profile.diploma}
                    </div>
                  )}
                </div>

                {(profile.caces || isEditing) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      CACES
                    </label>
                    {isEditing ? (
                      <div className="space-y-4">
                        <input
                          type="text"
                          value={editedProfile?.caces}
                          onChange={(e) => setEditedProfile(prev => ({
                            ...prev!,
                            caces: e.target.value
                          }))}
                          className="block w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                          placeholder="Numéro CACES"
                        />
                     
                      </div>
                    ) : (
                      <div className="flex items-center text-gray-900">
                        <CheckCircle className="h-5 w-5 text-gray-400 mr-2" />
                        {profile.caces} {profile.caces_type && `(${profile.caces_type})`}
                      </div>
                    )}
                  </div>
                )}

                <div>
            
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}